import { GlobalConfigType, setApplicationStep } from 'actions'
import cloneDeep from 'clone-deep'
import { FieldOrigin, InputType, IVisibleProp, LoanFieldProps } from 'config'
import { getEffectiveFieldRequiredForBorrower } from 'config/borrowerFieldRequired.util'
import { isCustomFieldKey } from 'config/loan.input.fields.constants'
import { store } from 'reducers'
import { InputValidate } from 'utils'
import { isLoanFieldPropHidden, removeInvisibleFields } from 'utils/loan'

import { BorrowerSeperator } from '../config'
import { borrowerMenuKey } from '../util'
import { defaultInputs } from './constants'

export const visibleBorrowerFieldsLogic = (borrowerSeperator: string) => {
  const { borrower: _borrower, globalConfigReducer, loan, loanDetail } = store.getState()
  const borrower = cloneDeep(_borrower)
  Object.keys(borrower).map((key) => {
    if (key !== 'borrower' && !borrower[key].isIncluded) delete borrower[key]
  })

  const loanFieldProperties = globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps
  const fieldProps = loanFieldProperties?.[FieldOrigin.BorrowerInformation]

  // When hasEntityTitle is hidden (visible=false), treat it as its defaultValue
  // rather than the actual stored borrower value, so dependent field logic is correct.
  const hasEntityTitleProp = fieldProps?.['hasEntityTitle']
  const effectiveHasEntityTitle =
    hasEntityTitleProp?.visible === false
      ? hasEntityTitleProp?.defaultValue ?? false
      : borrower[borrowerSeperator]?.hasEntityTitle

  let visibleFields: string[] = [
    'residency',
    'firstName',
    'middleName',
    'lastName',
    'email',
    'additionalEmails',
    'ssn',
    'phone',
    'cellPhone',
    'homePhone',
    'workPhone',
    'dob',
    'presentAddress',
    'mailingAddress',
    'ownership',
    'ownedRentedYears',
    'maritalStatus',
    'preferredLanguage',
    'hasEntityTitle',
  ]

  if (borrowerSeperator === BorrowerSeperator.BORROWER) {
    visibleFields.push(...['titleVesting', 'hasCoborrower'])
    if (loanDetail.includeCoborrower) {
      visibleFields.push('numOfCoBorrowers')
      for (let i = 1; i < Object.keys(borrower).length; i++) {
        visibleFields.push(`coborrower${i}Email`)
      }
    }
  }

  if (effectiveHasEntityTitle) {
    if (borrower[borrowerSeperator].borrowerType !== 'Individual') {
      visibleFields.push('borrowerPosition')
    }
    visibleFields.push('borrowerType')
    visibleFields.push('ownershipPercentage')
    visibleFields.push('isLayeredEntity')
    visibleFields.push('layerCount')
    visibleFields.push('entityTitle')
    visibleFields.push('entityAddress')
    visibleFields.push('entityTaxID')
    visibleFields.push('entityState')
    if (borrower[borrowerSeperator].borrowerType === 'Revocable Trust') {
      visibleFields.push('trustDate')
      visibleFields.push('settlorCount')
      const n = Math.max(0, Math.min(32, Number(borrower[borrowerSeperator].settlorCount) || 0))
      for (let i = 0; i < n; i++) {
        visibleFields.push(`settlor${i}`)
      }
    }
  }

  if (borrower[borrowerSeperator]?.ownedRentedYears === '1') {
    visibleFields.push('formerAddress')
    visibleFields.push('formerOwnership')
    visibleFields.push('formerOwnedRentedYears')
  }

  fieldProps &&
    Object.keys(fieldProps).map((key) => {
      if (!isCustomFieldKey(key)) return
      visibleFields.push(key)
    })

  return removeInvisibleFields(loan, FieldOrigin.BorrowerInformation, visibleFields)
}

export const additionalLayerInputsValidate = (
  borrowerSeperator: BorrowerSeperator,
  borrowerData?: Record<string, any>,
  loanFieldProperties?: LoanFieldProps,
) => {
  const { borrower: _borrower, globalConfigReducer } = store.getState()
  const borrower = borrowerData || _borrower[borrowerSeperator]
  const fieldProperties = loanFieldProperties || (globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps)
  const additionalLayerFields = (fieldProperties?.properties?.[FieldOrigin.BorrowerInformation]?.additionalInputs
    ?.additionalLayer || {}) as Record<string, IVisibleProp>
  const layerCount = Math.max(0, Number(borrower?.layerCount) || 0)
  const fields: Record<string, InputType>[] = []
  let success = true

  if (!borrower?.isLayeredEntity || layerCount === 0) return { success, fields }

  const borrowerInputs = defaultInputs()
  for (let layerIdx = 0; layerIdx < layerCount; layerIdx++) {
    const layerInputs: Record<string, InputType> = {}
    Object.keys(additionalLayerFields).forEach((key) => {
      const input = cloneDeep({
        ...(borrowerInputs[key] || {}),
        ...additionalLayerFields[key],
        value: borrower.additionalLayer?.[layerIdx]?.[key],
      } as InputType)
      const eff = getEffectiveFieldRequiredForBorrower(borrowerSeperator, input)
      if (eff !== undefined) input.required = eff

      if (isLoanFieldPropHidden(additionalLayerFields[key])) {
        input.visible = false
        input.error = ''
        layerInputs[key] = input
        return
      }

      input.error = InputValidate({ ...input, key })
      if (input.error?.length > 0) success = false
      layerInputs[key] = input
    })
    fields.push(layerInputs)
  }

  return { success, fields }
}

export const borrowerInfoValidate = (
  borrowerSeperator: BorrowerSeperator,
  visibles: any,
  fields: Record<string, InputType>,
  validateOnly = false,
  updateStore = false,
  afterRemoved = false,
) => {
  try {
    const { borrower: _borrower, step, globalConfigReducer, loanDetail } = store.getState()

    const borrower = cloneDeep(_borrower)
    Object.keys(borrower).map((key) => {
      if (key !== 'borrower' && !borrower[key].isIncluded) delete borrower[key]
    })

    const loanFieldProperties = globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps
    if (((loanFieldProperties as any)['visibility'] || {})[FieldOrigin.BorrowerInformation] === false) {
      if (validateOnly) return true
      else return {}
    }
    if (validateOnly) {
      visibles = visibleBorrowerFieldsLogic(borrowerSeperator)
      fields = defaultInputs()
    }
    const fieldProps = loanFieldProperties[FieldOrigin.BorrowerInformation]

    let success = true
    fields = cloneDeep(fields)
    Object.keys(fields).map((key) => {
      if (visibles.indexOf(key) === -1) {
        fields[key].error = ''
        return
      }

      if (fieldProps && fieldProps[key]) {
        const eff = getEffectiveFieldRequiredForBorrower(borrowerSeperator as BorrowerSeperator, fieldProps[key])
        if (eff !== undefined) fields[key].required = eff
      }

      let value = borrower[borrowerSeperator][key]
      if (isCustomFieldKey(key))
        value = borrower[borrowerSeperator]?.customInformation?.[FieldOrigin.BorrowerInformation]?.[key]
      else if (key === 'settlorCount') value = borrower[borrowerSeperator].settlorCount ?? 0
      else if (/^settlor\d+$/.test(key)) {
        const idx = Number(key.replace('settlor', ''))
        value = (borrower[borrowerSeperator].settlers || [])[idx] ?? ''
      }

      if (key.startsWith('coborrower') && key.endsWith('Email')) {
        const idx = Number(key.replace('coborrower', '').replace('Email', ''))
        const coBorrowerKey = Object.keys(borrower)[idx]
        value = _borrower[coBorrowerKey]?.email
      }

      const error = InputValidate({ ...fields[key], value })
      fields[key].value = value
      fields[key].error = error
      if (error.length > 0) {
        success = false
      }
      if (fieldProps && fieldProps[key] && fieldProps[key].tooltip) fields[key].tooltip = fieldProps[key].tooltip
    })

    if (!additionalLayerInputsValidate(borrowerSeperator, borrower[borrowerSeperator], loanFieldProperties).success) {
      success = false
    }

    if (borrowerSeperator === 'borrower') {
      const _hasEntityTitleProp = fieldProps?.['hasEntityTitle']
      const _effectiveHasEntityTitle =
        _hasEntityTitleProp?.visible === false
          ? _hasEntityTitleProp?.defaultValue ?? false
          : borrower[borrowerSeperator].hasEntityTitle
      if (_effectiveHasEntityTitle === true && visibles.indexOf('borrowerType') !== -1) {
        if (borrower[borrowerSeperator].borrowerType === 'Individual') {
          fields['borrowerType'].error = `Can not be 'Individual' when Has Entity Name.`
          success = false
        }
      }

      if (Number(loanDetail.rateData?.price) > 0) {
        ;['borrowerType', 'residency'].map((key) => {
          fields[key].disabled = true
          fields[key].tooltip = [
            'After selecting a price, you can only change it on the loan structure page.',
            fields[key].tooltip,
          ]
            .filter((v) => !!v)
            .join('\n\n')
        })
      }
    }

    if (visibles.indexOf('dob') !== -1 && fields['dob']?.value) {
      const dob = new Date(fields['dob'].value)
      const now = new Date()
      if (now.getFullYear() - dob.getFullYear() < 18) fields['dob'].error = 'Borrower must be at least 18 years old.'
    }

    if (afterRemoved) success = false
    if (updateStore) {
      let application = cloneDeep(step.application)
      const key = borrowerMenuKey(borrowerSeperator, 'borrower')
      application[key] = success ? 1 : -1
      store.dispatch(setApplicationStep(application))
    }
    if (validateOnly) return success

    return fields
  } catch (e) {
    console.log('borrowerInfoValidate error', e)
  }
}
